"use client";
import { Item } from "@andrew-wolf/react-photoswipe-gallery";
import VideoGalleryItem from "./VideoGalleryItem";
import PhotoGalleryItem from "./PhotoGalleryItem";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowUp,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useTranslations } from "next-intl";
import SelectableItem from "./MediaWrappers/SelectableItem";
import type { PhotoItemType, VideoItemType } from "./types";
import { MediaEnum } from "./enums";
import { paginationButtonsSxConfig } from "./sxConfig";
import { useEffect } from "react";
import { isVideoItem } from "./helpers";

export function DisplayItem(
  paginatedItems: any[],
  setPaginatedItemsState: (
    value: React.SetStateAction<(VideoItemType | PhotoItemType)[]>
  ) => void,
  handlePhotoVideoSeen: (item: VideoItemType | PhotoItemType) => Promise<void>,
  recentlySeenItems: string[],
  reservationId: string | null,
  timeZone?: string
  // pageNumber: number,
  // pageSize: number,
) {
  useEffect(() => {
    setPaginatedItemsState(paginatedItems);
  }, [paginatedItems]);

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-6 3xl:grid-cols-10 gap-2 pswp-gallery">
      {paginatedItems.map(
        (item: VideoItemType | PhotoItemType, index: number) => {
          const { id, url } = item;
          const getIsSelectable = () => {
            if (item.mediaType === MediaEnum.Photo) {
              return Boolean(item.thumbnail);
            }

            if (isVideoItem(item)) {
              return item.fileLength === item.uploaded;
            }

            return true;
          };

          return (
            <SelectableItem key={id} id={id} isSelectable={getIsSelectable()}>
              {isVideoItem(item) ? (
                <Item
                  original={item.url}
                  content={
                    <div className="w-full mx-auto h-[100vh] flex justify-center items-center">
                      <video
                        className="max-w-[100%] max-h-[100%] aspect-video"
                        preload="auto"
                        playsInline
                        controls
                        autoPlay
                        poster={item.thumbnail}
                        onEnded={() => handlePhotoVideoSeen(item)}
                      >
                        <source src={item.url} type="video/mp4" />
                      </video>
                    </div>
                  }
                >
                  {({ ref, open }) => (
                    <div>
                      <VideoGalleryItem
                        item={item}
                        index={index}
                        handleOpen={open}
                        videoRef={ref}
                        showQrStatus={Boolean(reservationId)}
                        recentlySeenItems={recentlySeenItems}
                        timeZone={timeZone}
                      />
                    </div>
                  )}
                </Item>
              ) : (
                <Item
                  original={url}
                  thumbnail={item.thumbnail}
                  width={item.width}
                  height={item.height}
                >
                  {({ ref, open }) => (
                    <PhotoGalleryItem
                      item={item}
                      imageRef={ref}
                      handleOpen={open}
                      index={index}
                      recentlySeenItems={recentlySeenItems}
                      key={`${item.id}_${item.uploaded}`}
                      showQrStatus={Boolean(reservationId)}
                      timeZone={timeZone}
                    />
                  )}
                </Item>
              )}
            </SelectableItem>
          );
        }
      )}
    </div>
  );
}

export function FirstPageButton({ onClick }: any) {
  const t = useTranslations();

  return (
    <IconButton onClick={onClick} sx={paginationButtonsSxConfig}>
      <KeyboardDoubleArrowUp />
      {t("Gallery.pagination.first")}
    </IconButton>
  );
}

export function PrevPageButton({ onClick }: any) {
  const t = useTranslations();
  return (
    <IconButton
      onClick={onClick}
      sx={{
        marginTop: "8px",
        marginBottom: "8px",
        marginLeft: "8px",
        ...paginationButtonsSxConfig,
      }}
    >
      <KeyboardArrowUp />
      {t("Gallery.pagination.previous")}
    </IconButton>
  );
}

export function NextPageButton({ onClick }: any) {
  const t = useTranslations();
  return (
    <IconButton
      onClick={onClick}
      sx={{
        marginTop: "8px",
        marginBottom: "8px",
        marginRight: "8px",
        ...paginationButtonsSxConfig,
      }}
    >
      <KeyboardArrowDown />
      {t("Gallery.pagination.next")}
    </IconButton>
  );
}

export function LastPageButton({ onClick }: any) {
  const t = useTranslations();
  return (
    <IconButton onClick={onClick} sx={paginationButtonsSxConfig}>
      <KeyboardDoubleArrowDown />
      {t("Gallery.pagination.last")}
    </IconButton>
  );
}

export function ProcessingUIElement() {
  const t = useTranslations();
  return (
    <div className="flex w-full h-full items-center justify-center">
      <p className="text-4xl text-white">{t("Gallery.contentProcessng")}</p>
    </div>
  );
}
